<template>
  <div class="article-content-step row">
    <div class="col-12 col-md-8 border-right">
      <div class="form-group">
        <label class="d-flex w-100 article-title" for="article-title">
          <span> Article Title </span>
          <span class="ml-auto text-lightgray"> {{ title.length }} / 80 </span>
        </label>
        <t-input
          class="form-control"
          type="text"
          id="article-title"
          :class="{
            'is-invalid': errors
              ? errors.title
              : highlightFields && (title.length > 80 || !this.title),
          }"
          placeholder="Enter title"
          v-model="title"
          @input="$emit('update', { title })"
        />
        <span
          class="title-length-warning"
          v-if="highlightFields && title.length > 80"
        >
          This field has an 80 character limit
        </span>
      </div>

      <div class="form-group">
        <label class="d-flex w-100 article-title" for="article-title">
          <span> Bullet 1 </span>
          <span class="ml-auto text-lightgray">
            {{ bullet_1.length }} / {{ bulletMaxLen }}
          </span>
        </label>
        <t-input
          class="form-control"
          type="text"
          id="article-bullet_1"
          :class="{
            'is-invalid': errors
              ? errors.bullet_1
              : highlightFields &&
                (!this.bullet_1 || bullet_1.length > bulletMaxLen),
          }"
          placeholder="Enter bullet 1"
          v-model="bullet_1"
          @input="$emit('update', { bullet_1 })"
        />
        <span
          class="bullet_1-length-warning"
          v-if="highlightFields && bullet_1.length > bulletMaxLen"
        >
          This field has an {{ bulletMaxLen }} character limit
        </span>
      </div>

      <div class="form-group">
        <label class="d-flex w-100 article-title" for="article-title">
          <span> Bullet 2 </span>
          <span class="ml-auto text-lightgray">
            {{ bullet_2.length }} / {{ bulletMaxLen }}
          </span>
        </label>
        <t-input
          class="form-control"
          type="text"
          id="article-bullet_2"
          :class="{
            'is-invalid': errors
              ? errors.bullet_2
              : highlightFields &&
                (!this.bullet_2 || bullet_2.length > bulletMaxLen),
          }"
          placeholder="Enter bullet 2"
          v-model="bullet_2"
          @input="$emit('update', { bullet_2 })"
        />
        <span
          class="bullet_2-length-warning"
          v-if="highlightFields && bullet_2.length > bulletMaxLen"
        >
          This field has an {{ bulletMaxLen }} character limit
        </span>
      </div>

      <div class="form-group">
        <label class="d-flex w-100 article-title" for="article-title">
          <span> Bullet 3 </span>
          <span class="ml-auto text-lightgray">
            {{ bullet_3.length }} / {{ bulletMaxLen }}
          </span>
        </label>
        <t-input
          class="form-control"
          type="text"
          id="article-bullet_3"
          :class="{
            'is-invalid': errors
              ? errors.bullet_3
              : highlightFields &&
                (!this.bullet_3 || bullet_3.length > bulletMaxLen),
          }"
          placeholder="Enter bullet 3"
          v-model="bullet_3"
          @input="$emit('update', { bullet_3 })"
        />
        <span
          class="bullet_3-length-warning"
          v-if="highlightFields && bullet_3.length > bulletMaxLen"
        >
          This field has an {{ bulletMaxLen }} character limit
        </span>
      </div>

      <div class="form-group">
        <label for="">Web synopsis</label>
        <HtmlEditor
          ref="webRef"
          v-model="webSynopsis"
          :class="{
            'is-invalid': errors
              ? errors.teaser_text
              : highlightFields && !this.webSynopsis,
          }"
          :token="`Bearer ${$login.getToken()}`"
          :image-upload-url="imageUploadUrl"
          :file-upload-url="fileUploadUrl"
          :enable-charts="true"
          @input="$emit('update', { webSynopsis })"
        />
      </div>

      <template v-if="isCorporateContentType || isSovereignContentType">
        <!-- CORPORATE BONDS -->
        <div class="form-group">
          <label for=""
            >{{ isCorporateContentType ? 'Corporate' : 'Sovereign' }} bond
            recommendations</label
          >
          <form-select
            :options="bonds"
            display-property="display_name"
            class="mt-1"
            :class="{
              'is-invalid': errors ? errors.bonds : false,
            }"
            placeholder="Enter bond ticker, bloomberg ticker, or ISIN"
            @changed="searchBonds"
            @input="addBond"
            async
            searchable
          >
            <template v-if="scope.option" slot="option" slot-scope="scope">
              <span>{{ scope.option.description }}</span>
            </template>
          </form-select>
        </div>

        <!-- Recommendations -->
        <div class="article-recommendations">
          <div
            v-for="(bond, bondIndex) in article.bonds"
            :key="bondIndex"
            class="article-recommendations__item"
          >
            <div
              v-if="bond.objectID"
              class="d-flex justify-content-between article-recommendations__item__header"
            >
              <h6>
                {{ bond.description }}
              </h6>
              <div class="recommendation-remove" @click="removeBond(bondIndex)">
                <span>Remove</span>
                <icon icon="close" />
              </div>
            </div>

            <div
              v-if="bond.objectID"
              class="d-flex article-recommendations__item__inputs"
            >
              <multiselect
                :value="
                  getProp(bond.recommendation, 'recommendation')
                    ? bond.recommendation.recommendation
                    : null
                "
                @select="(rec) => updateBondRecommendation(bondIndex, rec)"
                @remove="() => updateBondRecommendation(bondIndex)"
                :options="recommendations"
                :allow-empty="true"
                :show-labels="false"
                :searchable="false"
                :class="{
                  'is-invalid':
                    (errors && errors.companies && errors.companies[bondIndex]
                      ? errors.companies[bondIndex]
                      : []
                    ).length > 0,
                }"
                placeholder="Recommendation"
                class="recommendation-multiselect"
                style="cursor: pointer"
                select-label="Select"
                label="name"
                track-by="id"
              >
                <span slot="placeholder" class="mutliselect__placeholder">
                  Select recommendation
                </span>
              </multiselect>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <!-- COMPANIES -->
        <div class="form-group">
          <label for="">Securities/Companies</label>
          <form-select
            :options="companies"
            display-property="display_name"
            class="mt-1"
            :class="{ 'is-invalid': errors ? errors.companies : false }"
            placeholder="Enter company name, ticker or ISIN"
            @changed="searchCompanies"
            @input="addCompany"
            async
            searchable
          >
            <template v-if="scope.option" slot="option" slot-scope="scope">
              <span>
                {{ scope.option.display_name }}
              </span>
            </template>
          </form-select>
        </div>

        <!-- Recommendations -->
        <div class="article-recommendations">
          <div
            v-for="(company, companyIndex) in article.companies"
            :key="companyIndex"
            class="article-recommendations__item"
          >
            <div
              v-if="company.id"
              class="d-flex justify-content-between article-recommendations__item__header"
            >
              <h6>
                {{ company.name }}
              </h6>
              <div
                class="recommendation-remove"
                @click="removeRecommendation(companyIndex)"
              >
                <span>Remove</span>
                <icon icon="close" />
              </div>
            </div>
            <div
              v-if="company.id"
              class="d-flex article-recommendations__item__inputs"
            >
              <multiselect
                :value="
                  getProp(company.recommendation, 'recommendation')
                    ? company.recommendation.recommendation
                    : null
                "
                @select="
                  (rec) => updateCompanyRecommendation(companyIndex, rec)
                "
                @remove="() => updateCompanyRecommendation(companyIndex)"
                :options="recommendations"
                :allow-empty="true"
                :show-labels="false"
                :searchable="false"
                :class="{
                  'is-invalid':
                    (errors &&
                    errors.companies &&
                    errors.companies[companyIndex]
                      ? errors.companies[companyIndex]
                      : []
                    ).length > 0,
                }"
                placeholder="Recommendation"
                class="recommendation-multiselect"
                style="cursor: pointer"
                select-label="Select"
                label="name"
                track-by="id"
              >
                <span slot="placeholder" class="mutliselect__placeholder">
                  Select recommendation
                </span>
              </multiselect>

              <t-input
                type="number"
                class="article-recommendations__price"
                placeholder="Target Price"
                :value="company.recommendation.target_price"
                @change="(e) => updateRecommendationPrice(e, companyIndex)"
              />

              <multiselect
                v-if="currencies"
                :value="
                  getProp(company.recommendation, 'recommendation')
                    ? company.recommendation.currency
                    : null
                "
                @select="
                  (currency) =>
                    updateRecommendationCurrency(currency, companyIndex)
                "
                :options="currencies"
                :allow-empty="true"
                :show-labels="false"
                :searchable="true"
                placeholder="Currency"
                class="currency-multiselect"
                style="cursor: pointer"
                select-label="Select"
                label="code"
                track-by="id"
              >
                <template slot="option" slot-scope="props">
                  {{ props.option.symbol }} - {{ props.option.code }}
                </template>
              </multiselect>
              <div
                v-if="company.recommendation.toggleable"
                class="btn-group ml-2"
              >
                <button
                  @click="updateRecommendationAssetClass(5, companyIndex)"
                  class="btn-outline-primary"
                  :class="{
                    active: company.recommendation.asset_class_id === 5,
                  }"
                >
                  FI
                </button>
                <button
                  @click="updateRecommendationAssetClass(1, companyIndex)"
                  class="btn-outline-primary"
                  :class="{
                    active: company.recommendation.asset_class_id !== 5,
                  }"
                >
                  EQ
                </button>
              </div>
            </div>
            <div
              v-if="!company.objectID"
              class="d-none d-md-flex border-top article-recommendations__item__upload"
              @click="toggleModelSidebar(company)"
            >
              <icon icon="upload-file" />
              <span>{{
                company.model_filename || 'Upload company model'
              }}</span>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div class="col-12 col-md-4">
      <div class="form-group">
        <label for="">Action</label>
        <multiselect
          v-model="article.action"
          :options="actions"
          :show-labels="false"
          placeholder="Select an action"
          track-by="id"
          label="name"
          @select="(o) => updateAction(o.id)"
        />
      </div>
      <div class="form-group">
        <label for="">Analysts</label>
        <quick-selector
          :value="article.analysts"
          :options="analysts"
          :async="true"
          class="mt-1"
          placeholder="Add an author"
          @search-changed="delayGetAnalysts"
          @selection-changed="updateAnalysts"
        />
        <ul
          v-sortable="{ onSort: onAnalystsSort }"
          class="list-group list-group-flush list-group-sortable mb-3 mt-0"
        >
          <li
            v-for="(analyst, index) in article.analysts"
            :key="analyst.id"
            style="cursor: move"
            class="article__list-group-item list-group-item"
          >
            <div class="d-flex">
              <div
                style="width: 24px; height: 24px"
                class="align-self-center mr-2"
              >
                <img
                  :src="`${
                    analyst.user.picture_url ||
                    'https://cdn.tellimer.com/analysts/default-tellimer.svg'
                  }`"
                  class="rounded-circle img-circle-bg article__analyst__img"
                  width="24"
                />
              </div>
              <div class="align-self-center">
                {{ analyst.name }}
              </div>
              <div class="ml-auto">
                <icon
                  icon="remove-tag"
                  @click.native="() => removeAnalyst(index)"
                  style="cursor: pointer"
                  class="text-gray-600"
                />
              </div>
            </div>
          </li>
        </ul>
      </div>

      <div class="form-group">
        <span class="label">Tags</span>
        <div class="form-group">
          <quick-selector
            :value="article.tags"
            :options="tags"
            async
            :selected="[]"
            class="mt-1"
            :class="{
              'is-invalid': errors
                ? errors.tags
                : highlightFields && !article.tags.length,
            }"
            placeholder="Enter a tag"
            @search-changed="searchTags"
            @selection-changed="updateTags"
          />
        </div>
        <div class="tag-input__list mt-2">
          <div
            v-for="(tag, index) in article.tags"
            :key="index"
            class="tag-input__list__item"
          >
            <span>
              {{ tag.name }}
            </span>
            <icon
              v-tooltip="'Remove'"
              @click.native="removeTag(index)"
              icon="remove-tag"
            />
          </div>
        </div>
      </div>
      <div v-if="!article.id" class="form-group mt-3">
        <span class="label">Automatically added tags</span>
        <div class="tag-input__list mt-2">
          <div
            v-for="(tag, index) in untaggableTags"
            :key="index"
            class="tag-input__list__item untaggable"
          >
            <span>
              {{ tag.name }}
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="
          article.article_layout &&
          ['standard-with-image', 'full-width-poster'].includes(
            article.article_layout.icon
          )
        "
        class="form-group mt-3"
      >
        <label for="">Upload Image</label>
        <img
          v-if="article.hero_image_url"
          :src="article.hero_image_url"
          :alt="article.hero_image_url"
          class="d-block mb-2 w-100"
        />
        <FileUpload
          :accept="['image/png', 'image/jpeg']"
          :headers="headers()"
          :value="
            article.hero_image_url
              ? [{ name: article.hero_image_url, url: article.hero_image_url }]
              : null
          "
          @input="heroImageUploaded"
          :action="url() + 'articles/image'"
        />
      </div>

      <div
        v-if="
          article.article_layout &&
          article.article_layout.icon === 'standard-with-video'
        "
        class="form-group mt-3"
      >
        <label for="">Vimeo video link</label>
        <t-input
          placeholder="Vimeo video link"
          :value="article.video"
          @input="(video) => $emit('update', { video })"
        />
      </div>

      <!-- Upload key chart -->
      <!-- <div class="form-group mt-3"> -->
      <!--   <label for="">Upload key chart</label> -->
      <!--   <img -->
      <!--     v-if="article.key_chart_url" -->
      <!--     :src="article.key_chart_url" -->
      <!--     :alt="article.key_chart_url" -->
      <!--     class="d-block mb-2 w-100" -->
      <!--   > -->
      <!--   <FileUpload -->
      <!--     :accept="['image/png', 'image/jpeg']" -->
      <!--     :headers="headers()" -->
      <!--     :value="article.key_chart_url ? [{name: article.key_chart_url, url: article.key_chart_url}] : null" -->
      <!--     @input="chartUploaded" -->
      <!--     :action="url() + 'upload/chart'" -->
      <!--   /> -->
      <!-- </div> -->

      <div v-if="isEditor" class="mt-3">
        <label for="">Disclaimer text</label>
        <div>
          <form-select
            :options="disclaimerTexts"
            display-property="name"
            :value="disclaimerText"
            @input="updateDisclaimerTextId"
            class="mt-1"
            :class="{
              'is-invalid': errors ? errors.disclaimer_text_id : false,
            }"
            searchable
          />
        </div>
      </div>

      <label class="mt-3">
        <ToggleButton
          type="checkbox"
          v-model="article.is_pro_exclusive"
          @change="toggleProExclusive"
          :color="{ checked: '#0080a8', unchecked: '#c2c7d9' }"
          :sync="true"
        />
        <span v-if="article.is_pro_exclusive"> Pro </span>
        <span v-else> Premium </span>
      </label>
    </div>
    <CompanyModelContainer
      @update="companyModelEdited"
      ref="company-model-upload"
    />
  </div>
</template>

<script>
import algolia from '@/algolia'
import { getOnce, search, debounce, getProp } from '@/helpers'
import QuickSelector from '@/components/QuickSelector'
import Sortable from '@/directives/sortable'
import gateway from '@/gateway'
import { FormSelect, FileUpload, HtmlEditor } from '@frontend/components'
import Checkbox from '@/components/Checkbox/Checkbox'
import { ToggleButton } from 'vue-js-toggle-button'

import CompanyModelContainer from './CompanyModelContainer'
import gql from 'graphql-tag'

const ASSET_CLASS_EQ = 1
const ASSET_CLASS_FI = 5

export default {
  components: {
    QuickSelector,
    FileUpload,
    FormSelect,
    HtmlEditor,
    Checkbox,
    ToggleButton,
    CompanyModelContainer,
  },

  directives: {
    Sortable,
  },

  props: {
    canPublish: {
      type: Boolean,
      default: false,
    },
    isEditor: {
      type: Boolean,
      default: false,
    },
    article: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      default: () => null,
    },
    highlightFields: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      bulletMaxLen: 120,
      debounce,
      title: this.article.title || '',
      bullet_1: this.article.bullet_1 || '',
      bullet_2: this.article.bullet_2 || '',
      bullet_3: this.article.bullet_3 || '',
      emailBody: this.article.email_body || '',
      webSynopsis: this.article.teaser_text || '',
      actions: [],
      companies: [],
      bonds: [],
      articleCompanies: [],
      currencies: [],
      recommendations: [],
      analysts: [],
      tags: [],
      chartList: [],
      titleLengthWarning: false,
      imageUploadUrl: process.env.VUE_APP_API_ENDPOINT + 'articles/assets',
      fileUploadUrl: process.env.VUE_APP_API_ENDPOINT + 'upload/file',
      disclaimerTexts: [],
      disclaimerText: null,
      canToggleProExclusive: this.$login.can('articles.toggle-pro-exclusive'),
    }
  },

  created() {
    this.getData()
    const tags = this.article.channel_content_type.content_type.tags
    if (tags && tags.length > 0 && this.article.tags.length === 0) {
      this.$emit('update', { tags: [...tags] })
    }
  },

  async mounted() {
    const result = await this.$apollo.query({
      fetchPolicy: 'network-only',
      query: gql`
        query($channelId: Int!) {
          disclaimerTexts(channelId: $channelId) {
            id
            name
            channelId
            contentTypeId
            analystId
          }
        }
      `,
      variables: {
        channelId: this.article.channel.id,
      },
    })

    this.disclaimerTexts = result.data.disclaimerTexts

    if (!this.article.disclaimer_text_id) {
      const dt = this.getDefaultDisclaimerText()
      this.updateDisclaimerTextId(dt)
    } else {
      this.disclaimerText = this.disclaimerTexts.find(
        (dt) => dt.id === this.article.disclaimer_text_id
      )
    }
  },

  watch: {
    title() {
      if (!this.article.is_pro_exclusive) {
        this.article.public_title = this.article.title
      }
    },
    bullet_1() {
      if (!this.article.is_pro_exclusive) {
        this.article.marketing_bullet_1 = this.article.bullet_1
      }
    },
    bullet_2() {
      if (!this.article.is_pro_exclusive) {
        this.article.marketing_bullet_2 = this.article.bullet_2
      }
    },
    bullet_3() {
      if (!this.article.is_pro_exclusive) {
        this.article.marketing_bullet_3 = this.article.bullet_3
      }
    },
    webSynopsis() {
      this.$emit('update', { teaser_text: this.webSynopsis })
      if (!this.article.is_pro_exclusive) {
        this.$emit('update', { public_teaser_text: this.webSynopsis })
      }
    },

    emailBody() {
      this.$emit('update', { email_body: this.emailBody })
    },
  },

  computed: {
    insightsUrl() {
      // return 'https://articles-insights-pr-1109.prestaging.tellimer.io/graphql'
      const stagingUrlSection = 'tellimer.io'
      return (
        window.location.host.includes(stagingUrlSection) ||
        process.env.VUE_APP_API_ENDPOINT.includes(stagingUrlSection)
      ) ? 'https://articles-insights.tellimer.io/graphql' : 'https://tellimer.com/graphql'
    },
    isCorporateContentType() {
      if (!this.article.channel_content_type) {
        return false
      }

      return this.article.channel_content_type.content_type.name
        .toLowerCase()
        .includes('corporate')
    },

    isSovereignContentType() {
      return (
        !this.isCorporateContentType &&
        this.article.channel_content_type.content_type.name
          .toLowerCase()
          .includes('sovereign')
      )
    },

    isEditorOrTellimerAnalyst() {
      return this.$login.can('articles.view')
    },
    untaggableTags() {
      if (this.article.id) {
        return
      }
      const result = []
      const map = new Map()

      const tags = []
      this.article.analysts.forEach((analyst) => {
        tags.push(analyst.tag)
      })

      this.article.companies.forEach((company) => {
        // tags.push(company.tag)
        // tags.push(company.country.tag)
        // tags.push(company.country.major_region.tag)
        // tags.push(company.micro_sector.sector.tag)
      })

      if (this.article.channel) {
        tags.push({ name: this.article.channel.name })
      }

      if (this.article.channel_content_type) {
        if (this.article.channel_content_type.channel) {
          tags.push({ name: this.article.channel_content_type.channel.name })
        }
      }

      for (const tag of tags) {
        if (!map.has(tag.id)) {
          map.set(tag.id, true) // set any value to Map
          result.push({
            id: tag.id,
            name: tag.name,
          })
        }
      }

      return result
    },
  },

  methods: {
    updateDisclaimerTextId(disclaimerText) {
      if (disclaimerText) {
        this.disclaimerText = disclaimerText
        this.$emit('update', { disclaimer_text_id: disclaimerText.id })
      }
    },

    getDefaultDisclaimerText() {
      const matchesContentType = this.disclaimerTexts.filter(
        (dt) =>
          dt.contentTypeId === this.article.channel_content_type.content_type.id
      )

      if (matchesContentType) {
        const def = matchesContentType.find((dt) => dt.analystId === null)
        const analyst = this.article.analysts[0]
          ? matchesContentType.find(
              (dt) => dt.analystId === this.article.analysts[0].id
            ) || def
          : def
        if (analyst) {
          return analyst
        }
        if (def) {
          return def
        }
      }

      const analystOnly = this.article.analysts[0]
        ? matchesContentType.find(
            (dt) =>
              dt.analystId === this.article.analysts[0].id && !dt.contentTypeId
          )
        : null

      return (
        analystOnly ||
        this.disclaimerTexts.find(
          (dt) =>
            dt.channelId === this.article.channel.id &&
            !dt.analystId &&
            !dt.contentTypeId
        ) ||
        this.disclaimerTexts.find((dt) => !dt.channelId)
      )
    },

    headers() {
      return {
        Authorization: 'Bearer ' + this.$login.getToken(),
      }
    },

    url() {
      return process.env.VUE_APP_API_ENDPOINT
    },

    companyModelEdited(rs) {
      const { companies = [] } = this.article
      const temp = companies.map((company) => {
        if (company.id === rs.id) {
          return {
            ...company,
            model_filename: rs.model_filename || null,
            model_path: rs.model_path || null,
          }
        }
        return company
      })

      this.$emit('update', {
        companies: temp,
      })
    },

    chartUploaded(r) {
      const response = r[0]
      if (response) {
        this.$emit('update', {
          key_chart: response.name,
          key_chart_url: response.url,
        })
      } else {
        this.$emit('update', {
          key_chart: null,
          key_chart_url: null,
        })
      }
    },

    heroImageUploaded(r) {
      const response = r[0]
      if (response) {
        this.$emit('update', {
          hero_image: response.name,
          hero_image_url: response.url,
        })
      } else {
        this.$emit('update', {
          hero_image: null,
          hero_image_url: null,
        })
      }
    },

    searchTags: debounce(function (query) {
      const index = algolia.initIndex('tags')
      index.search(
        {
          query,
          hitsPerPage: 10,
        },
        (err, { hits } = {}) => {
          this.tags = hits.map((hit) => {
            hit.id = hit.objectID

            return hit
          })
        }
      )
    }, 300),

    getProp,

    async getData() {
      const [
        recommendationsResponse,
        currenciesResponse,
        actionsResponse,
      ] = await Promise.all([
        getOnce(`recommendations?channel_id=${this.article.channel.id}`),
        getOnce('currencies'),
        gateway.get('/cms/actions'),
      ])
      if (recommendationsResponse?.data?.data?.length > 0) {
        const closePreviousRecommendation = {...recommendationsResponse.data.data[0]}
        closePreviousRecommendation.id = 0;
        closePreviousRecommendation.name = 'End previous recommendation';
        recommendationsResponse.data.data.push(closePreviousRecommendation)
      }
      this.recommendations = recommendationsResponse.data.data.map((r) => ({
        ...r,
        suggested_by: this.$login.getUser().id,
      }))
      this.currencies = currenciesResponse.data.data
      this.actions = actionsResponse.data.data.map(({ id, name }) => ({
        id,
        name,
      }))
    },

    async searchCorporateBonds(query) {
      const index = algolia.initIndex('bonds')
      const response = await index.search(query, {
        facetFilters: ['type:COR', 'provider:SandP'],
      })

      this.bonds = response.hits
    },

    async searchSovereignBonds(query) {
      const index = algolia.initIndex('bonds')
      const response = await index.search(query, {
        facetFilters: ['provider:SandP'],
      })

      this.bonds = response.hits
    },

    async searchBonds(query) {
      if (this.isCorporateContentType) {
        return this.searchCorporateBonds(query)
      }

      return this.searchSovereignBonds(query)
    },

    async searchCompanies(query) {
      const convert = (v) => {
        let display_name = v.name
        if (v.tickersymbol) {
          display_name += ` - ${v.tickersymbol}`
        }
        if (v.exchangesymbol) {
          display_name += ` - ${v.exchangesymbol}`
        }

        return {
          company: {
            ...v,
          },
          display_name,
        }
      }
      const queries = [
        {
          indexName: 'equities',
          query,
          params: {
            hitsPerPage: 5,
          },
        },
        // {
        //   indexName: 'security',
        //   query,
        //   params: {
        //     hitsPerPage: 5,
        //   },
        // },
      ]
      algolia.search(queries, (err, { results }) => {
        const companies = results[0].hits.map(convert)
        this.companies = [
          ...companies,
          // ...results[1].hits.filter(
          //   (hit) =>
          //     !companies.find((c) => hit.security_code_isin === c.company.isin)
          // ),
        ]
      })
    },

    async updateBonds(bonds = []) {
      this.$emit('update', {
        bonds,
      })
    },

    async updateRecommendations(companies = []) {
      this.$emit('update', {
        companies,
      })
    },

    getAssetClassIdBasedOnCompany(company) {
      const fi = !!this.article.tags.find((tag) =>
        tag.name.includes('Fixed Income')
      )
      const eq = !!this.article.tags.find((tag) => tag.name.includes('Equity'))

      if (eq) {
        return ASSET_CLASS_EQ
      }

      if (fi) {
        return ASSET_CLASS_FI
      }

      if (company.security && company.security) {
        // MarketDB FI
        if (company.security_asset_class_id === 1) {
          return ASSET_CLASS_FI
        }
      }
    },

    async addBond(bond) {
      const { bonds = [] } = this.article
      bonds.push({
        ...bond,
        recommendation: {},
      })
      this.updateBonds(bonds)
    },

    async addCompany(company) {
      try {
        let {data} = await this.$http.post(this.insightsUrl,
            JSON.parse(`{"query":"query SAndPCompany($sAndPCompanyId: Int!) {\\n  sAndPCompany(id: $sAndPCompanyId) {\\n id \\n    isQuasiSov\\n    hasCapitalStructure\\n    hasFinancials\\n    hasFilings\\n    type\\n    slug\\n    name\\n    followTagId\\n    location\\n    locationAndSector\\n    region\\n    sector\\n    about\\n    logo\\n    logoFallback\\n  }\\n}","variables":{"sAndPCompanyId":${company.company.snp_id}},"operationName":"SAndPCompany"}`),
        )

        data = (data.data.sAndPCompany)
        const { companies = [] } = this.article
        companies.push({
          ...data,
          recommendation: {},
          security_id: company.objectID,
          security: company.display_name,
          security_asset_class_id: company.asset_class_type_id,
        })
        this.$emit('update', { companies })
      } catch (error) {
        throw new Error(error)
      }
    },

    removeBond(index) {
      const bonds = [...this.article.bonds]
      bonds.splice(index, 1)
      this.updateBonds(bonds)
    },

    removeRecommendation(companyIndex) {
      const recommendations = this.article.companies.filter(
        (c, index) => index !== companyIndex
      )
      this.updateRecommendations(recommendations)
    },

    updateBondRecommendation(bondIndex, recommendation = {}) {
      const bonds = this.article.bonds.map((bond, index) => {
        if (index === bondIndex) {
          bond.recommendation = recommendation
            ? {
                recommendation,
              }
            : recommendation
        }
        return bond
      })

      this.updateBonds(bonds)
    },

    updateCompanyRecommendation(companyIndex, recommendation = {}) {
      const recommendations = this.article.companies.map((company, index) => {
        if (index === companyIndex) {
          const acId = this.getAssetClassIdBasedOnCompany(company)
          company.recommendation = recommendation
            ? {
                recommendation,
                asset_class_id: acId || ASSET_CLASS_EQ,
                toggleable: acId ? false : true,
              }
            : recommendation
        }
        return company
      })
      this.updateRecommendations(recommendations)
    },

    updateRecommendationAssetClass(val, companyIndex) {
      const companies = [...this.article.companies]
      companies[companyIndex].recommendation.asset_class_id = val
      this.$emit('update', { companies })
    },

    updateRecommendationPrice(val, companyIndex) {
      const companies = [...this.article.companies]
      companies[companyIndex].recommendation.target_price = +val
      this.$emit('update', { companies })
    },

    updateRecommendationCurrency(currency, companyIndex) {
      const companies = [...this.article.companies]
      companies[companyIndex].recommendation.currency = currency
      this.$emit('update', { companies })
    },

    toggleModelSidebar(company = null) {
      if (company) {
        this.$refs['company-model-upload'].setCompany(company)
        this.$refs['company-model-upload'].toggle()
      }
    },

    onActionSelect(action) {
      this.$emit('update', { action })
    },

    updateAction(actionId) {
      const action = this.actions.find(({ id }) => id === actionId)
      this.$emit('update', { action })
    },

    delayGetAnalysts: debounce(async function (search) {
      // const {data} = await search('analysts', query)
      const params = {
        search: encodeURIComponent(search),
      }
      if (!this.canPublish) {
        params.channelIds = [this.$login.channelScopeId()]
      }
      const response = await gateway.get(`cms/analysts`, {
        params,
      })
      this.analysts = response.data.data.map((analyst) => {
        analyst.name = analyst.user.name
        return analyst
      })
    }, 200),

    onAnalystsSort(sort) {
      const analyst = this.article.analysts[sort.oldIndex]
      const { analysts } = this.article
      analysts.splice(sort.oldIndex, 1)
      analysts.splice(sort.newIndex, 0, analyst)

      this.$emit('update', { analysts })
    },

    removeAnalyst(index) {
      const { analysts = [] } = this.article
      analysts.splice(index, 1)
      this.$emit('update', { analysts })
    },

    updateAnalysts(analysts) {
      this.$emit('update', { analysts })
    },

    addTag(tag) {
      if (tag.name === 'Spotlight' && !this.article.hero_image_url) {
        return alert('Please add an image before adding the Spotlight tag')
      }
      this.$emit('add-tag', tag)
    },

    updateTags(tags) {
      const spotlightIndex = tags.findIndex((t) => t.name === 'Spotlight')
      if (spotlightIndex !== -1 && !this.article.hero_image_url) {
        alert('Please add an image before adding the Spotlight tag')
        tags.splice(spotlightIndex, 1)
      }
      this.$emit('update', { tags })
    },

    removeTag(index) {
      const { tags = [] } = this.article
      tags.splice(index, 1)
      this.$emit('update', { tags })
    },

    getCompanyTitle(company) {
      return `${company.name} - ${company.security}`
    },

    toggleProExclusive() {
      // set starter value to pro whenever this toggle is called
      this.$emit('update', {
        public_title: this.article.title,
        marketing_bullet_1: this.article.bullet_1,
        marketing_bullet_2: this.article.bullet_2,
        marketing_bullet_3: this.article.bullet_3,
        public_teaser_text: this.article.teaser_text,
      })
    },
  },
}
</script>

<style lang="scss">
.article-content-step {
  position: relative;
}

.article-title {
  .text-lightgray {
    font-size: 14px;
    line-height: 20px;
  }
}

.article-recommendations__item {
  margin-bottom: 1rem;
  background: #fbfbfb;
  border: 1px solid $border-color;

  &__header {
    padding: 0.5rem 1rem;
    h6 {
      color: #101820;
      font-size: 16px;
      flex: 1 1;
    }

    .recommendation-remove {
      font-size: 12px;
      color: #d93819;
      cursor: pointer;

      .i {
        margin-left: 4px;
        width: 8px;
        height: 8px;
      }
    }
  }

  &__inputs {
    padding: 0.5rem 1rem;
    @media (max-width: 576px) {
      flex-direction: column;
    }

    .multiselect {
      margin-left: 0.5rem;
      flex: 1;
    }

    .recommendation-multiselect {
      margin-left: 0;
      flex: 2;
    }

    .article-recommendations__price {
      height: 40px;
      margin-left: 0.5rem;
      flex: 1;

      .input__inner {
        border-radius: 0;
      }

      @media (max-width: 576px) {
        margin-top: 10px;
        margin-left: 0;
      }
    }

    .currency-multiselect {
      @media (max-width: 576px) {
        margin-top: 10px;
        margin-left: 0;
      }
    }
  }

  &__upload {
    padding: 0.5rem 1rem;
    color: #0080a8;
    cursor: pointer;
    .i {
      margin-right: 0.5rem;
      color: #0080a8 !important;
      fill: #0080a8 !important;
    }
  }
}

.slide-panel {
  .form-group {
    flex: 1;
    display: flex;
    flex-direction: column;

    .el-upload,
    .el-upload-100,
    .el-upload-dragger {
      height: 100%;
    }

    .el-upload-100 {
      height: calc(100% - 4rem);
    }
  }
}

.article-html {
  &--invalid {
    // .fr-toolbar {
    //   border-left-color: $red;
    //   border-right-color: $red;
    // }
    // .fr-toolbar.fr-top {
    //   border-top-color: $red;
    // }
    // .fr-box.fr-basic.fr-top .fr-wrapper {
    //   border-color: $red;
    // }
  }
}

.title-length-warning {
  color: #d9331a;
}
</style>
